import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import {
  LayoutDashboard, Users, Briefcase, FileText, Receipt,
  BarChart3, Settings, LogOut, Menu, X, FileSpreadsheet, Package, Landmark, CircleHelp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLocale } from '../contexts/LocaleContext';

const navItems = [
  { path: '/', labelKey: 'dashboard', icon: LayoutDashboard },
  { path: '/clients', labelKey: 'clients', icon: Users },
  { path: '/projects', labelKey: 'projects', icon: Briefcase },
  { path: '/invoices', labelKey: 'invoices', icon: FileText },
  { path: '/credit-notes', labelKey: 'creditNotes', icon: FileText },
  { path: '/expenses', labelKey: 'expenses', icon: Receipt },
  { path: '/banking', labelKey: 'banking', icon: Landmark },
  { path: '/templates', labelKey: 'templates', icon: Package },
];

const reportsSubNav = [
  { path: '/reports', labelKey: 'reports', icon: BarChart3 },
  { path: '/reports/accountant-handover', labelKey: 'accountantHandover', icon: FileSpreadsheet },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLocale();
  const { hasFeature } = usePlan();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isSuperAdmin = user && user.role === 'super_admin';
  const hasSettingsAccess = user && ['super_admin', 'admin', 'account_owner'].includes(user.role);
  
  // Check if user has accountant handover feature
  const hasAccountantHandover = hasFeature('accountant_handover');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white flex flex-col transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-700">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center font-bold text-sm">
            SF
          </div>
          <span className="text-lg font-semibold tracking-tight">ServiceFlow</span>
          <button className="ml-auto lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const label = t(item.labelKey);
            const isActive = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.labelKey.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {label}
              </Link>
            );
          })}
          
          {/* Reports Submenu */}
          <div className="space-y-1">
            {reportsSubNav.map((item) => {
              const label = t(item.labelKey);
              // Skip Accountant Handover if not available in plan
              if (item.path === '/reports/accountant-handover' && !hasAccountantHandover) {
                return null;
              }
              
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.labelKey.toLowerCase()}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {label}
                </Link>
              );
            })}
          </div>

          {/* Settings (account-level) */}
          {hasSettingsAccess && (
            <Link
              to="/settings"
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-settings"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                location.pathname === '/settings'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              {t('settings')}
            </Link>
          )}

          <Link
            to="/help"
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-help"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
              location.pathname === '/help' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <CircleHelp className="w-5 h-5 flex-shrink-0" />
            {t('help')}
          </Link>

          {/* Admin (super_admin only) */}
          {isSuperAdmin && (
            <Link
              to="/admin"
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-admin"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                location.pathname === '/admin' || location.pathname === '/super-admin'
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              {t('admin')}
            </Link>
          )}
        </nav>

        <div className="px-3 py-4 border-t border-slate-700">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-xs font-medium">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors duration-200"
          >
            <LogOut className="w-5 h-5" />
            {t('signOut')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header for mobile */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
            <Menu className="w-6 h-6 text-slate-700" />
          </button>
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-bold">SF</span>
          </div>
          <span className="font-semibold text-slate-900">ServiceFlow</span>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
